import React, { useEffect, useState } from "react";
import { Loader2, Globe, FileText, Sparkles, CheckCircle2, XCircle } from "lucide-react";

/**
 * Centered modal loading screen for long-running operations
 * like website parsing / product scanning / training.
 *
 * Props:
 *   open: bool
 *   title: string                — e.g. "Training your agent"
 *   subtitle: string             — e.g. "Reading hishoppers.com…"
 *   steps: [{ label, status }]   — status: 'pending' | 'active' | 'done' | 'error'
 *   onClose?: () => void         — called when status becomes 'done' (auto-close)
 *   variant?: 'training' | 'scanning'
 */
export default function ParseLoadingModal({ open, title, subtitle, steps = [], variant = "training" }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const i = setInterval(() => setTick((t) => t + 1), 80);
    return () => clearInterval(i);
  }, [open]);

  if (!open) return null;

  const accent = variant === "scanning" ? "#10B981" : "#0044FF";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#030712]/70 backdrop-blur-sm" data-testid="parse-loading-modal">
      <div className="bg-white max-w-md w-full mx-4 shadow-2xl border-2 border-[#030712]">
        {/* Animated header */}
        <div className="p-6 border-b-2 border-[#030712] relative overflow-hidden">
          <div className="relative flex items-center gap-3">
            <div className="relative">
              <Loader2 className="w-7 h-7 animate-spin" style={{ color: accent }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-3 h-3" style={{ color: accent }} />
              </div>
            </div>
            <div className="flex-1">
              <div className="font-display font-extrabold text-lg uppercase tracking-tight">{title}</div>
              {subtitle && <div className="text-[11px] font-mono text-[#4B5563] mt-0.5 truncate">{subtitle}</div>}
            </div>
          </div>
          {/* Scanning dots */}
          <div className="flex gap-1 mt-3">
            {Array.from({ length: 32 }).map((_, i) => {
              const isActive = ((tick / 3) | 0) % 32 === i;
              return (
                <div
                  key={i}
                  className="h-0.5 flex-1 transition-colors"
                  style={{ background: isActive ? accent : "#E2E8F0" }}
                />
              );
            })}
          </div>
        </div>

        {/* Steps */}
        {steps.length > 0 && (
          <div className="p-6 space-y-3">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <StepIcon status={s.status} accent={accent} />
                <span className={`${s.status === "done" ? "text-[#4B5563] line-through" : s.status === "active" ? "text-[#030712] font-medium" : s.status === "error" ? "text-[#EF4444]" : "text-[#9CA3AF]"}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="px-6 pb-5">
          <div className="flex items-center gap-2 text-[10px] font-mono text-[#4B5563]">
            {variant === "scanning" ? <Globe className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
            {variant === "scanning"
              ? "discovering products via sitemap + JSON-LD"
              : "rendering · extracting · chunking · embedding"}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepIcon({ status, accent }) {
  if (status === "done") return <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" />;
  if (status === "error") return <XCircle className="w-4 h-4 text-[#EF4444] shrink-0" />;
  if (status === "active") return <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: accent }} />;
  return <div className="w-4 h-4 rounded-full border-2 border-[#E2E8F0] shrink-0" />;
}
